import React from 'react';
import { Download, FileText, CheckCircle } from 'lucide-react';

const GeneratedEssay = ({ templateData, generatePDF, handleTemplateChange }) => {
  // Essential fields for Chinese essay completion
  const essentialFields = ['subject', 'mainBody'];
  const isComplete = essentialFields.every(field => templateData[field] && templateData[field].trim() !== '');
  const completionCount = Object.values(templateData).filter(value => value.trim() !== '').length;

  // Quick fill function for testing
  const quickFillSample = () => {
    handleTemplateChange('subject', '我的夢想');
    handleTemplateChange('mainBody', '每個人都有自己的夢想，我的夢想是成為一名優秀的作家。從小我就喜歡閱讀各種書籍，被那些精彩的故事深深吸引。我希望有一天也能用自己的筆觸創造出感動人心的作品，讓讀者在我的文字中找到共鳴和啟發。為了實現這個夢想，我會努力學習寫作技巧，多閱讀優秀的文學作品，不斷提升自己的文學素養。');
    handleTemplateChange('opening', '夢想是人生的指路明燈，指引我們前進的方向。');
    handleTemplateChange('conclusion', '我相信只要堅持不懈地努力，總有一天我的夢想會實現。');
  };
  
  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div 
        className="relative rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(147, 51, 234, 0.9), rgba(219, 39, 119, 0.9)), url(https://images.unsplash.com/photo-1471107340929-a87cd0f5b5f3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1973&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <h2 className="text-3xl font-bold mb-3">作文預覽</h2>
          <p className="text-purple-100 text-lg">
            檢視您的作文並在準備好時下載為 PDF
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-2xl font-bold text-gray-900">您的文件</h3>
              <p className="text-gray-600">
                {isComplete ? (
                  <span className="flex items-center space-x-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>完成並準備下載</span>
                  </span>
                ) : (
                  <span>{completionCount}/10 項已完成 (需要: 主題和內容)</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {!isComplete && (
              <button
                onClick={quickFillSample}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200"
              >
                <FileText className="w-4 h-4" />
                <span>快速填入範例</span>
              </button>
            )}
            <button
              onClick={generatePDF}
              disabled={!isComplete}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                isComplete 
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Download className="w-5 h-5" />
              <span>下載 PDF</span>
            </button>
          </div>
        </div>

        {/* Document Preview */}
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
            <div 
              className="bg-white border-2 border-gray-200 rounded-lg p-8 shadow-inner min-h-96"
              style={{ 
                backgroundImage: 'linear-gradient(90deg, transparent 74px, #e5e7eb 74px, #e5e7eb 76px, transparent 76px)',
                backgroundSize: '100% 24px'
              }}
            >
              {/* Document Header */}
              <div className="mb-8 pb-4 border-b-2 border-gray-300">
                <h1 className="text-xl font-bold text-center text-gray-800 mb-4">
                  {templateData.type.toUpperCase().replace('-', ' ')} 作文
                </h1>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                  <div><strong>日期：</strong> {templateData.date || '[日期]'}</div>
                  <div><strong>主旨：</strong> {templateData.subject || '[主旨]'}</div>
                  <div><strong>收件人：</strong> {templateData.recipient || '[收件人]'}</div>
                  <div><strong>寄件人：</strong> {templateData.sender || '[寄件人]'}</div>
                </div>
              </div>

              {/* Content Sections */}
              <div className="space-y-6 text-gray-800 leading-relaxed">
                <div className="space-y-1">
                  <div className="text-blue-600 font-medium text-lg">
                    {templateData.greeting || '[問候語]'}
                  </div>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-green-600 uppercase tracking-wide">開頭段落</h4>
                  <p className="text-gray-800">
                    {templateData.opening || '[開頭段落 - 說明您的寫作目的]'}
                  </p>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-purple-600 uppercase tracking-wide">主要內容</h4>
                  <div className="text-gray-800 whitespace-pre-line">
                    {templateData.mainBody || '[主要內容 - 以連貫、有條理的段落呈現您的詳細內容]'}
                  </div>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-orange-600 uppercase tracking-wide">結論段落</h4>
                  <p className="text-gray-800">
                    {templateData.conclusion || '[結論段落 - 禮貌的最後訊息和結尾備註]'}
                  </p>
                </div>

                <div className="space-y-1 mt-8">
                  <div className="text-red-600 font-medium whitespace-pre-line">
                    {templateData.closing || '[結尾敬語 - 應與稱呼相符]'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Tips */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                寫作技巧
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• 確保您的語調符合讀者和目的</li>
                <li>• 商業書信使用正式語言，朋友間使用非正式語言</li>
                <li>• 保持段落專注於一個主要想法</li>
                <li>• 校對語法、拼寫和標點符號</li>
                <li>• 確保您的結尾與開頭稱呼相符</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">格式指南</h4>
              <div className="text-sm text-gray-700 space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span>問候語/稱呼</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span>開頭段落 - 寫作目的</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded"></div>
                  <span>主要內容 - 核心內容</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-orange-500 rounded"></div>
                  <span>結論段落 - 最後想法</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span>結尾敬語 - 簽名結束</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratedEssay;
